import * as T from 'three';
import URDFLoader from 'urdf-loader';
import { FileUploader, Select, Checkbox, Button, Slider, Container, TextInput } from '../ui/pageElements';
import { getURDFFromURL, getURDFFromLocal, loadJsonURL } from '../robotFunctions/loaderHelper';
import { LineInterface } from './lineInterface';
import { RobotGroup } from './robotGroup';
import { getDesaturatedColor, recurseMaterialTraverse } from '../robotFunctions/robotHelpers';

const HEADER_CONTAINER = "robotListHeaderContainer";

/**
 * This is the robot interface, which holds all the robot groups.
 * 
 */
export class RobotInterface {
    constructor(scene, interfaces) {
        this.manager = new T.LoadingManager();
        this.loader = new URDFLoader(this.manager);

        // collisions and visuals
        this.loader.parseVisual = true;
        this.loader.parseCollision = false;

        this.focusSelector;
        this.scene = scene;
        this.interfaces = interfaces;
        this.allRobots = [];
        this.focusJoint;
        this.selectedRobotGroup;
        this.idCounter = 0;

        this.interfaces.lines = new LineInterface(this.scene, this.interfaces);
        this.topBlock = new Container("topBlock", "topDiv");
        this.robotSelector = new Select("Robot Selector", HEADER_CONTAINER);
        this.robotSelector.setToolTip(this.interfaces.tooltips.robotSelector, "leftToolTip");

        this.clickCheckDiv = new Container("ClickSelectContainer", HEADER_CONTAINER);
        this.clickSelectCheck = new Checkbox("Click Select", HEADER_CONTAINER, true);
        this.clickSelectCheck.setToolTip(this.interfaces.tooltips.clickSelect, "leftToolTip");
        this.clickCheckDiv.addBlock(this.clickSelectCheck);

        this.robotLoader = new FileUploader("Upload Robot", "topDiv", [], true);
        this.robotLoader.setToolTip(this.interfaces.tooltips.uploadRobotFile, "topToolTip");
        this.robotLoader.fileUploader.onchange = () => {
            if (this.robotLoader.getFiles().length > 0) {
                getURDFFromLocal(this.robotLoader.getFiles(), (blob) => {
                    this.loadRobot(URL.createObjectURL(blob))
                });
            }
        }

        this.urlInput = new TextInput("URDF URL", "topDiv");
        this.urlInput.setToolTip(this.interfaces.tooltips.urdfLink, "topToolTip");

        this.loadURL = new Button("Load URDF URL", "topDiv", () => {
            getURDFFromURL(this.urlInput.textInput.value, (blob) => {
                this.loadRobot(URL.createObjectURL(blob), this.urlInput.textInput.value)
            });
        })
        this.loadURL.setToolTip(this.interfaces.tooltips.urdfLinkButton, "topToolTip");

        this.robotSelector.select.onchange = () => {
            this.setRobot(this.robotSelector.getSelectedName());
        }

        this.topBlock.addBlock(this.robotLoader, this.urlInput, this.loadURL);
    }

    loadRobot(robotFile, url = null, afterFunction = () => {}) {
        let robot;

        this.loader.load(robotFile, result => {
            robot = result;
        });
        this.manager.onLoad = () => {
            robot.robotName = robot.robotName + "_" + this.idCounter
            this.idCounter++;
            robot.traverse(c => {
                c.castShadow = true;
                c.recieveShadow = true;
                if(c.type == "PointLight") {
                    c.intensity = 0;
                    c.castShadow = false;
                    c.distance = 0;
                }
                if (c.material) {
                    recurseMaterialTraverse(c.material, (material) => {
                        material.alphaToCoverage = true;
                        material.transparent = true;
                        material.baseColor = material.color.clone();
                        material.highlightColor = material.color.clone().add(new T.Color(0.2, 0.2, 0.4));
                        material.desaturatedColor = getDesaturatedColor(material.color.clone());
                        material.color = material.desaturatedColor;
                        material.side = T.DoubleSide
                    })
                }
            });

            this.allRobots.forEach(group => {
                group.animationInterface.addParallelOption(robot)
            })

            let robotGroup = new RobotGroup(robot, this.interfaces, url)
            this.allRobots.push(robotGroup);

            if (this.allRobots.length == 1) {
                this.selectedRobotGroup = this.allRobots[0];
                this.interfaces.animation = this.selectedRobotGroup.animationInterface;
                this.interfaces.joints = this.selectedRobotGroup.jointInterface;
                this.interfaces.animation.setFocusJoint(this.focusJoint);
            } else {
                robotGroup.animationInterface.hideUI();
                robotGroup.jointInterface.hideUI();
            }

            this.robotSelector.addOption([robot.robotName, robot]);
            afterFunction();
        }
    }

    getScene() {
        return this.scene;
    }

    getSelectedRobot() {
        return this.selectedRobotGroup.robot;
    }

    getSelectedRobotGroup() {
        return this.selectedRobotGroup;
    }

    getRobotGroup(robotName) {
        return this.allRobots.find(group => group.robotName == robotName)
    }

    getInterfaces() {
        return this.interfaces;
    }
    
    getAllRobots() {
        return this.allRobots.map(group => group.robot);
    }

    getAllRobotGroups() {
        return this.allRobots;
    }

    setRobot(robotName) {
        // hide ui
        this.selectedRobotGroup.animationInterface.hideUI()
        this.selectedRobotGroup.jointInterface.hideUI();
        this.selectedRobotGroup.unmarkDiv();

        // update
        let robotIndex = this.allRobots.findIndex(group => group.robot.robotName == robotName);
        this.selectedRobotGroup = this.allRobots[robotIndex];
        this.interfaces.animation = this.selectedRobotGroup.animationInterface;
        this.interfaces.animation.showUI();
        this.interfaces.joints = this.selectedRobotGroup.jointInterface;
        this.interfaces.joints.showUI();
        this.selectedRobotGroup.markDiv();
        this.robotSelector.select.selectedIndex = robotIndex;

        this.allRobots.forEach(group => {
            group.updateColors();
        })
    }

    getHoveredRobotGroup() {
        let hoveredGroup = null;
        let meshes = [];
        this.allRobots.forEach(group => {
            if(group.hoverableCheck.checkbox.checked) {
                meshes = meshes.concat(group.meshes);
            }
        })
        let intersects = this.interfaces.raycaster.intersectObjects(meshes, false);
        if(intersects.length > 0) {
            this.allRobots.forEach(group => {
                let robotMeshes = group.meshes;
                if(robotMeshes.includes(intersects[0].object)) {
                    if(this.clickSelectCheck.checkbox.checked) {
                        hoveredGroup = group;
                    }
                }
            })
        }
        return hoveredGroup;
    }

    updateAllRobots() {
        this.allRobots.forEach(robotGroup => {
            robotGroup.highlightRobot(false);
            robotGroup.updateEndEffector();
        });
        let hoveredRobotGroup = this.getHoveredRobotGroup();
        if(hoveredRobotGroup) {
            hoveredRobotGroup.highlightRobot(true);
        }
        if(this.selectedRobotGroup) {
            this.selectedRobotGroup.animationInterface.animateSlider();
            this.selectedRobotGroup.animationInterface.updateTimeBar();
        }
    }

    robotClick() {
        if(this.allRobots.length > 0 && this.clickSelectCheck.checkbox.checked) {
            let hoveredRobotGroup = this.getHoveredRobotGroup();
            if(hoveredRobotGroup) {
                this.setRobot(hoveredRobotGroup.robot.robotName);
            }
        }
    }

    setClickSelect(enabled) {
        this.clickSelectCheck.checkbox.checked = enabled
    }

    getClickSelectSetting() {
        return this.clickSelectCheck.checkbox.checked
    }
}